/**
 * The access token lives in memory only (sellsmart-api#4626, ADR-2).
 *
 * The long-lived credential is the API's HttpOnly refresh cookie, which no
 * script can read; this module holds the short-lived access token for the
 * life of the page. A reload starts empty and `restoreSession()` in
 * utils/request.ts gets a new token from the cookie.
 */

let accessToken: string | null = null;
let sessionSub: string | null = null;

/** The `sub` claim of a JWT, or null when it cannot be read. */
export const subOf = (token: string): string | null => {
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const sub = JSON.parse(json)?.sub;
    return typeof sub === "string" || typeof sub === "number" ? String(sub) : null;
  } catch {
    return null;
  }
};

export const getAccessToken = (): string | null => accessToken;

/** Store a token for the session this tab holds (login, or a refresh of it). */
export const setAccessToken = (token: string | null): void => {
  accessToken = token;
  sessionSub = token ? subOf(token) : null;
};

/**
 * Store a refreshed token, unless it belongs to another user (AC-1.15). One
 * browser holds one refresh cookie per portal, so a second sign-in or
 * impersonation in another tab replaces it; this tab must then sign out
 * rather than silently become the other user.
 */
export const adoptRefreshedToken = (token: string): boolean => {
  const sub = subOf(token);
  if (sessionSub !== null && sub !== sessionSub) {
    return false;
  }
  setAccessToken(token);
  return true;
};

const isLoopback = (host: string): boolean =>
  ["localhost", "127.0.0.1", "[::1]", "::1"].includes(host.toLowerCase());

const SITE = ".sellsmart.com.au";

/**
 * True when the API is same-site with this page, so the browser sends the
 * SameSite=Lax refresh cookie to it. A fixed suffix, not a "last two labels"
 * guess, which would read `com.au` for every Australian host.
 */
export const isSameSiteApi = (
  page: string = typeof window === "undefined" ? "" : window.location.hostname,
): boolean => {
  if (!page || !process.env.URL_API) {
    return false;
  }
  try {
    const api = new URL(process.env.URL_API).hostname;
    if (isLoopback(page) || isLoopback(api)) {
      return isLoopback(page) && isLoopback(api);
    }
    return page.endsWith(SITE) && api.endsWith(SITE);
  } catch {
    return false;
  }
};

/**
 * Headers for a request that starts or migrates a session. The marker tells
 * the API this portal keeps the token in memory and uses the cookie, so the
 * session is never refreshed by bearer.
 */
export const refreshCookieHeaders = (): Record<string, string> =>
  isSameSiteApi() ? { "X-SS-Refresh-Cookie": "1" } : {};
